<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">Payrolls</h1>
        <app-button @click.native="onOpenCreatePayrollPopup" size="small">Create payroll</app-button>
      </app-cells>
      <div v-for="table_item of table.items" :key="table_item.name" class="client-block">
        <app-cells v-if="table_item.name">
          <h2 class="title">{{ table_item.name }}</h2>
          <div v-if="table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__legal_entity><div class="table-default__left">Legal entity</div></template>
          <template #legal_entity="{ row }">
            <div class="table-default__left">
              <router-link :to=" { name: 'employees-payroll-employees', params: { id: row.id } }" class="table-default__link-border">
                {{ row.legal_entity.short_name }}
              </router-link>
            </div>
          </template>
          <template #month="{ row }">{{ dateFormat(row.month) }}</template>
          <template #gross="{ row }">{{ row.gross.toLocaleString() }} TSh</template>
          <template #accrued_salary="{ row }">{{ row.accrued_salary.toLocaleString() }} TSh</template>
          <template #nssf_amount="{ row }">{{ row.nssf_amount.toLocaleString() }} TSh</template>
          <template #nhif_amount="{ row }">{{ row.nhif_amount.toLocaleString() }} TSh</template>
          <template #paye_amount="{ row }">{{ row.paye_amount.toLocaleString() }} TSh</template>
          <template #wcf_amount="{ row }">{{ row.wcf_amount.toLocaleString() }} TSh</template>
          <template #wht_amount="{ row }">{{ row.wht_amount.toLocaleString() }} TSh</template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="10" />
      </app-cells>
    </template>
    <modal
      name="create-payroll-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('create-payroll-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Create payroll</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckCreatePayrollForm">
            <app-form-group label="Legal entity" required>
              <app-select
                v-model="form_payroll.legal_entity"
                :options="entities_options"
                :reduce="item => item.code"
                :clearable="false"
                label="short_name"
                placeholder="Legal entity"
                :error="$v.form_payroll.legal_entity.$error"
              />
              <template #error>
                <div v-if="$v.form_payroll.legal_entity.$dirty && !$v.form_payroll.legal_entity.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="payroll_submit" :disabled="$v.form_payroll.$error">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getAdministrativeLegalEntities, getEmployeesPayrollDatesList, postEmployeesPayroll } from '@/http'
import { format, parse } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'EmployeesPayrollList',
  data() {
    return {
      is_loaded: false,
      table_preloader: true,
      table_length: 0,
      table: {
        items: [],
        columns: ['legal_entity', 'month', 'employees_pax', 'gross', 'accrued_salary', 'nssf_amount', 'nhif_amount', 'paye_amount', 'wcf_amount', 'wht_amount'],
        options: {
          headings: {
            legal_entity: 'Legal entity',
            month: 'Month',
            employees_pax: 'Employees',
            gross: 'Gross',
            accrued_salary: 'Accrued salary',
            nssf_amount: 'NSSF',
            nhif_amount: 'NHIF',
            paye_amount: 'PAYE',
            wcf_amount: 'WCF',
            wht_amount: 'WHT',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form_payroll: {},
      entities_options: [],
    }
  },
  created() {
    this.fetchItems()
    getAdministrativeLegalEntities({ employment: true }).then(res => { this.entities_options = res.data })
  },
  validations: {
    form_payroll: {
      legal_entity: { required }
    }
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getEmployeesPayrollDatesList({
        page: this.pagination.page,
        page_size: 10,
        group_by: 'month',
        range_type: 'month',
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        res.data.results.forEach(item => {
          this.table_length += item.items.length
        })

        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onCheckCreatePayrollForm() {
      this.$v.form_payroll.$touch()
      if (this.$v.form_payroll.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendCreatePayrollForm()
      }
    },
    sendCreatePayrollForm() {
      this.$refs.payroll_submit.preload = true
      postEmployeesPayroll(this.form_payroll).then(res => {
        this.$router.push({ name: 'employees-payroll-employees', params: { id: res.data.id } })
      })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy')
    },
    onOpenCreatePayrollPopup() {
      this.form_payroll = {}
      this.$v.form_payroll.$reset()
      this.$modal.show('create-payroll-popup')
    },
  }
}
</script>

<style lang="sass" scoped>
.client-block
  margin-bottom: 30px

  &:last-child
    margin-bottom: 0
</style>
